import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { HttpHeadersService } from '../http-headers/http-headers.service';
import {
  PostResponseProjectDocumentsRequestBody,
  ResponseProjectDocumentsApiResponse,
  ResponseProjectDocument,
  DbResponseProjectDocument,
  LIBRARY_DOCUMENT_FIELDS,
  LIBRARY_PAGE_SIZE,
  LIBRARY_SORT_ORDER_BY,
  LibrarySortField,
  buildLibraryFilters,
} from '../../../models/response-project-document';

export interface CreateDocumentRequestBody {
  id?: number;
  file_size: number;
  name: string;
  response_project_id?: number;
  response_document_subtype_id?: number;
  is_in_library?: boolean;
  custom_name?: string;
}

export interface CreateDocumentResponse {
  id: number;
  presigned_url: string;
  status: string;
  uid: string;
  content_type: string;
}

export interface UpdateDocumentRequestBody {
  id: number;
  status?: string;
  response_document_subtype_id?: number;
  custom_name?: string;
  is_in_library?: boolean;
  response_project_id?: number | null;
}

export interface UpdateDocumentResponse {
  message: string;
  response_project_document_id: number;
}

export interface ResponseProjectDocumentsResult {
  data: ResponseProjectDocument[];
  totalCount?: number;
}

@Injectable({
  providedIn: 'root',
})
export class ApiResponseDocumentsService {
  private url = `${environment.explainApiUrl}response_documents`;

  constructor(
    private http: HttpClient,
    private httpHeadersService: HttpHeadersService
  ) {}

  search(body: PostResponseProjectDocumentsRequestBody): Observable<ResponseProjectDocumentsResult> {
    const payload: PostResponseProjectDocumentsRequestBody = { ...body, paginate: body.paginate ?? true };
    return this.http.post<ResponseProjectDocumentsApiResponse>(this.url, payload, this.httpHeadersService.httpHeaders).pipe(
      map(res => ({
        data: (res.data ?? []).map(item => this.toResponseProjectDocument(item)),
        totalCount: res.pagination?.total_item_count,
      }))
    );
  }

  searchLibraryPage(query: string, subtypeIds: number[], page: number, sort: LibrarySortField): Observable<ResponseProjectDocumentsResult> {
    return this.search({
      fields: [...LIBRARY_DOCUMENT_FIELDS],
      filters: buildLibraryFilters(query, subtypeIds),
      limit: LIBRARY_PAGE_SIZE,
      offset: page,
      paginate: true,
      order_by: LIBRARY_SORT_ORDER_BY[sort],
    });
  }

  getDocument(id: number): Observable<ResponseProjectDocument> {
    return this.http
      .get<DbResponseProjectDocument>(`${this.url}?id=${id}`, this.httpHeadersService.httpHeaders)
      .pipe(map(item => this.toResponseProjectDocument(item)));
  }

  countInLibrary(extraFilters: Record<string, unknown> = {}): Observable<number> {
    return this.search({
      fields: ['id'],
      filters: { is_in_library: true, ...extraFilters },
      limit: 1,
      offset: 0,
      paginate: true,
    }).pipe(map(res => res.totalCount ?? 0));
  }

  /** Ids of the library documents the indexer is still working on, to reconcile stale rows against. */
  findPendingIndexationInLibrary(): Observable<ResponseProjectDocumentsResult> {
    return this.search({
      fields: ['id'],
      filters: { is_in_library: true, indexation_status: ['pending'] },
      limit: 500,
      offset: 0,
      paginate: false,
    });
  }

  /**
   * Library documents the indexer gave up on, with their status. Read once a row leaves the
   * pending set, so a settled row can be told apart from a successful one without a request each.
   */
  findFailedIndexationInLibrary(): Observable<ResponseProjectDocumentsResult> {
    return this.search({
      fields: ['id', 'indexation_status'],
      filters: { is_in_library: true, indexation_status: ['failure', 'not_indexable'] },
      limit: 500,
      offset: 0,
      paginate: false,
    });
  }

  findUncategorizedInLibrary(undefinedSubtypeId: number): Observable<ResponseProjectDocumentsResult> {
    return this.search({
      fields: [...LIBRARY_DOCUMENT_FIELDS, 'file_size'],
      filters: { is_in_library: true, response_document_subtype_id: undefinedSubtypeId },
      limit: 200,
      offset: 0,
      paginate: true,
      order_by: [{ field: 'name', direction: 'asc' }],
    });
  }

  countLibraryBySubtype(subtypeIds: number[]): Observable<Record<number, number>> {
    if (!subtypeIds.length) return of({});
    return forkJoin(
      subtypeIds.map(id => this.countInLibrary({ response_document_subtype_id: id }).pipe(map(count => [id, count] as const)))
    ).pipe(map(pairs => Object.fromEntries(pairs)));
  }

  findByNameInProject(name: string, projectId: string): Observable<ResponseProjectDocumentsResult> {
    return this.search({
      fields: ['id', 'name', 'response_document_subtype_id', 'is_in_library'],
      filters: { name, response_project_id: Number(projectId) },
      limit: 50,
      offset: 0,
      paginate: false,
    });
  }

  findByNameInLibrary(name: string): Observable<ResponseProjectDocumentsResult> {
    return this.search({
      fields: ['id', 'name', 'response_document_subtype_id', 'is_in_library', 'response_projects', 'uploader'],
      filters: { name, is_in_library: true },
      limit: 50,
      offset: 0,
      paginate: false,
    });
  }

  createDocument(body: CreateDocumentRequestBody): Observable<CreateDocumentResponse> {
    return this.http.put<CreateDocumentResponse>(this.url, body, this.httpHeadersService.httpHeaders);
  }

  updateDocument(body: UpdateDocumentRequestBody): Observable<UpdateDocumentResponse> {
    return this.http.patch<UpdateDocumentResponse>(this.url, body, this.httpHeadersService.httpHeaders);
  }

  deleteDocument(id: number): Observable<void> {
    return this.http.delete<void>(`${this.url}?id=${id}`, this.httpHeadersService.httpHeaders);
  }

  unlinkFromProject(documentId: number, projectId: number): Observable<void> {
    return this.http.delete<void>(
      `${environment.explainApiUrl}response_project_documents?response_document_id=${documentId}&response_project_id=${projectId}`,
      this.httpHeadersService.httpHeaders
    );
  }

  uploadToPresignedUrl(presignedUrl: string, file: File, contentType: string): Observable<number> {
    return new Observable(subscriber => {
      const req = this.http
        .request<unknown>('PUT', presignedUrl, {
          body: file,
          headers: { 'Content-Type': contentType },
          observe: 'events',
          reportProgress: true,
        })
        .subscribe({
          next: event => {
            if (event.type === HttpEventType.UploadProgress && event.total != null && event.total > 0) {
              const percent = Math.round((100 * event.loaded) / event.total);
              subscriber.next(percent);
            } else if (event.type === HttpEventType.Response) {
              subscriber.next(100);
              subscriber.complete();
            }
          },
          error: err => subscriber.error(err),
        });
      return () => req.unsubscribe();
    });
  }

  private toResponseProjectDocument(item: DbResponseProjectDocument): ResponseProjectDocument {
    const rawProjects = item.response_projects;
    return {
      id: String(item.id),
      uid: item.uid,
      name: item.name,
      customName: item.custom_name ?? null,
      fileSize: item.file_size,
      s3Key: item.s3_key,
      responseDocumentSubtypeId: item.response_document_subtype_id,
      status: item.status,
      indexationStatus: item.indexation_status ?? null,
      isInLibrary: item.is_in_library,
      projects: rawProjects?.map(p => ({
        id: p.id,
        name: p.name,
        is_active: p.is_active,
      })),
      uploader: item.uploader ? { id: item.uploader.id, name: item.uploader.name } : null,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      downloadPresignedUrl: item.download_presigned_url ?? null,
    };
  }
}
